/**
 * GradedMonadExplainer — progressive-disclosure explainer for graded monads.
 *
 * A graded monad `T_κ` is a family of endofunctors indexed by a monoid
 * `(κ, ·, e)` of "grades": every monadic step pays a grade, and Kleisli
 * composition combines them via the monoid product. CSK's PRECOMMIT (W) is
 * the canonical example.
 *
 * The interactive grade ladder in the CSK-relevance tier lets the reader
 * combine two effectful steps and see the resulting grade rise — never
 * fall — by the monoid multiplication. The forbidden move (grade going
 * down) is rendered as a structurally inexpressible arrow.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useState } from "react";
import { InteractiveDemoBox } from "../../components/InteractiveDemoBox.js";
import { OptionRadioGroup, type OptionRadioChoice } from "../../components/OptionRadioGroup.js";
import { FONT, GRAY, THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  GradeLadderDiagram,
  KleisliCompositionDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
  type GradeLadderRung,
} from "../shared/index.js";

const ACCENT = THEOREM_COLOR.PRECOMMIT;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-graded-monad",
  kind: "Categorical structure",
  title: "Graded monad",
  subtitle: "T_κ : κ → [C, C] indexed by a monoid (κ, ·, e)",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Monad", href: "#explainer-monad" },
  { label: "Comonad", href: "#explainer-comonad" },
  { label: "Bimonad", href: "#explainer-bimonad" },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "PRECOMMIT (W)", href: "#explainer-graded-monad", accent: ACCENT },
];

// inline: local
//
// A simple permission/commitment grade monoid for the demo. The grade ladder
// uses `read < write < admin`, and grades combine by the join (the maximum
// requirement seen so far). The identity `e = read` represents "no extra
// commitment". The point of the demo is *not* to teach a particular grade
// algebra — it is to show that Kleisli composition always climbs.
const GRADES = ["read", "write", "admin"] as const;
type Grade = (typeof GRADES)[number];

function gradeIndex(grade: Grade): number {
  return GRADES.indexOf(grade);
}

function joinGrades(a: Grade, b: Grade): Grade {
  return gradeIndex(a) >= gradeIndex(b) ? a : b;
}

const LADDER_RUNGS: readonly GradeLadderRung[] = GRADES.map((g) => {
  const note =
    g === "read"
      ? "identity grade · e"
      : g === "write"
        ? "writes a record"
        : "alters permission table";
  return { grade: g, note };
})
  .slice()
  .reverse();

const STEP_CHOICES: readonly OptionRadioChoice<Grade>[] = [
  { value: "read", label: "Fetch the user profile (read)" },
  { value: "write", label: "Append an audit-log entry (write)" },
  { value: "admin", label: "Rotate the signing key (admin)" },
];

function GradeLadderInteractive(): React.JSX.Element {
  const [first, setFirst] = useState<Grade>("read");
  const [second, setSecond] = useState<Grade>("write");
  const combined = useMemo(() => joinGrades(first, second), [first, second]);

  const handleFirst = useCallback((g: Grade) => {
    setFirst(g);
  }, []);
  const handleSecond = useCallback((g: Grade) => {
    setSecond(g);
  }, []);

  return (
    <InteractiveDemoBox accent={ACCENT} ariaLabel="Graded Kleisli composition demo">
      <p
        style={{
          margin: "0 0 0.5rem",
          fontSize: "0.8125rem",
          color: GRAY[700],
          fontFamily: FONT.body,
        }}
      >
        Pick a grade for each Kleisli step. The composite climbs to the larger of the two — it never
        falls below either.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "0.5rem",
          alignItems: "stretch",
        }}
      >
        <OptionRadioGroup<Grade>
          legend="Step 1: f : A → T_κ₁(B)"
          choices={STEP_CHOICES}
          value={first}
          onChange={handleFirst}
          accent={ACCENT}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: ACCENT,
            fontWeight: 700,
            fontSize: "1.1rem",
            fontFamily: FONT.mono,
          }}
          aria-hidden="true"
        >
          <span>·</span>
          <span style={{ fontSize: "0.625rem", color: GRAY[600] }}>monoid product</span>
        </div>
        <OptionRadioGroup<Grade>
          legend="Step 2: g : B → T_κ₂(C)"
          choices={STEP_CHOICES}
          value={second}
          onChange={handleSecond}
          accent={ACCENT}
        />
      </div>

      <div
        style={{
          marginTop: "0.875rem",
          padding: "0.625rem 0.75rem",
          backgroundColor: "white",
          border: `1px solid ${withAlpha(ACCENT, "44")}`,
          borderRadius: "0.375rem",
          textAlign: "center",
          fontFamily: FONT.mono,
          fontSize: "0.9375rem",
        }}
        aria-live="polite"
      >
        <MathInline ariaLabel="g composed with f at grade kappa-1 times kappa-2">
          {tex`g \circ_{T} f : A \to T_{\mathrm{${first}} \cdot \mathrm{${second}}}(C)`}
        </MathInline>
        <span style={{ margin: "0 0.5rem", color: GRAY[500] }}>=</span>
        <strong style={{ color: ACCENT }}>
          <MathInline>{tex`T_{\mathrm{${combined}}}`}</MathInline>
        </strong>
      </div>
      <p
        style={{
          margin: "0.5rem 0 0",
          fontSize: "0.8125rem",
          color: GRAY[600],
          fontStyle: "italic",
        }}
      >
        Grades never go down: the seam at{" "}
        <MathInline>{tex`\mathrm{admin} \to \mathrm{write}`}</MathInline> is structurally
        inexpressible because there is no Kleisli arrow in the graded category that decreases the
        grade.
      </p>
    </InteractiveDemoBox>
  );
}

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>graded monad</strong> is a monad whose envelope <em>remembers how much it cost</em>.
      Each effectful step carries a grade <MathInline>{tex`\kappa`}</MathInline> drawn from a
      monoid; Kleisli composition multiplies the grades. CSK uses graded monads to make "commitments
      accumulate and never vanish" a structural fact, not a comment.
    </p>
  ),
  intuition: (
    <>
      <p>
        Imagine an ordinary monad as a single drawer that holds your effects. A graded monad
        replaces the drawer with a <em>filing cabinet</em>: every drawer is labelled with a grade,
        and the grade tells you exactly which discipline that drawer enforces — read-only,
        writes-allowed, admin-only, "needs the precommit", "spends a budget unit", and so on.
      </p>
      <p>
        When you sequence two steps, you do <strong>not</strong> get to choose a smaller drawer for
        the combined arrow. You get the drawer indexed by{" "}
        <MathInline>{tex`\kappa_{1} \cdot \kappa_{2}`}</MathInline>, the monoid product of the two
        grades. If <MathInline>{tex`\cdot`}</MathInline> is the maximum (a lattice join), grades
        only ever rise. If <MathInline>{tex`\cdot`}</MathInline> is concatenation of a commitment
        list, the list only grows. Either way, the cost of a composite is exactly the cost it
        deserves — and the compiler can read it off the type.
      </p>
      <p>
        Ordinary monads recover as the degenerate case{" "}
        <MathInline>{tex`\kappa = \{e\}`}</MathInline>: one grade, one drawer, the usual story.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`C`}</MathInline> be a category and{" "}
        <MathInline>{tex`(\kappa, \cdot, e)`}</MathInline> a monoid (more generally a monoidal
        category — see <code>ACGradingCategory</code> in MathLibCore). A{" "}
        <strong>graded monad</strong> on <MathInline>{tex`C`}</MathInline> indexed by{" "}
        <MathInline>{tex`\kappa`}</MathInline> is a family
      </p>
      <MathBlock ariaLabel="T from kappa to the endofunctor category of C">
        {tex`T : \kappa \to [C, C]`}
      </MathBlock>
      <p>
        of endofunctors <MathInline>{tex`T_{k} : C \to C`}</MathInline> for each{" "}
        <MathInline>{tex`k \in \kappa`}</MathInline>, together with:
      </p>
      <ul>
        <li>
          <strong>Graded unit</strong>{" "}
          <MathInline>{tex`\eta : \mathrm{Id} \Rightarrow T_{e}`}</MathInline>: a pure value carries
          the identity grade.
        </li>
        <li>
          <strong>Graded multiplication</strong>{" "}
          <MathInline>{tex`\mu_{k,\ell} : T_{k} \circ T_{\ell} \Rightarrow T_{k \cdot \ell}`}</MathInline>
          : combining two grades produces the monoid product.
        </li>
      </ul>
      <p>
        Satisfying graded analogues of the unit and associativity laws, and naturality in{" "}
        <MathInline>{tex`k, \ell`}</MathInline>.
      </p>
      <GradeLadderDiagram rungs={LADDER_RUNGS} accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>
        A <strong>graded Kleisli arrow</strong> from <MathInline>{tex`A`}</MathInline> to{" "}
        <MathInline>{tex`B`}</MathInline> at grade <MathInline>{tex`k`}</MathInline> is a morphism{" "}
        <MathInline>{tex`f : A \to T_{k}(B)`}</MathInline>. Composition is
      </p>
      <MathBlock ariaLabel="g composed with f in the Kleisli category at grade k times ell">
        {tex`g \circ_{T} f : A \to T_{k \cdot \ell}(C)`}
      </MathBlock>
      <p style={{ textAlign: "center", color: GRAY[600], fontSize: "0.85rem" }}>
        (for <MathInline>{tex`f`}</MathInline> at grade <MathInline>{tex`k`}</MathInline> and{" "}
        <MathInline>{tex`g`}</MathInline> at <MathInline>{tex`\ell`}</MathInline>)
      </p>
      <KleisliCompositionDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>
        The Kleisli category becomes <em>graded</em>: hom-sets are indexed by{" "}
        <MathInline>{tex`\kappa`}</MathInline>, and composition is the monoid product. When{" "}
        <MathInline>{tex`\kappa`}</MathInline> is a partially ordered monoid you also get{" "}
        <em>weakening</em> <MathInline>{tex`T_{k} \Rightarrow T_{k'}`}</MathInline> whenever{" "}
        <MathInline>{tex`k \leq k'`}</MathInline> — useful for relaxing a known-tighter grade to fit
        a callsite.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        Graded monads are the structural home of <strong>PRECOMMIT (W)</strong>, the Neutralization
        Theorem for "future cannot bless past". Three concrete payoffs:
      </p>
      <ul>
        <li>
          <strong>Commitments accumulate by type, not by runtime check.</strong> A program that has
          issued two commitments lives at grade <MathInline>{tex`k_{1} \cdot k_{2}`}</MathInline>.
          Discharging requires producing the witness for <em>that</em> grade — the type system
          refuses any shortcut.
        </li>
        <li>
          <strong>Forbidden grade-decreasing arrows are inexpressible.</strong> There is no map{" "}
          <MathInline>{tex`T_{\mathrm{admin}}(A) \to T_{\mathrm{read}}(A)`}</MathInline> in the
          graded Kleisli category when <MathInline>{tex`\cdot`}</MathInline> is a join. Authority
          laundering by "forgetting which drawer the value came from" cannot be named.
        </li>
        <li>
          <strong>The seam adjusts to the grade monoid.</strong> Replace{" "}
          <MathInline>{tex`\kappa`}</MathInline> with a list of audit obligations and you get an
          audit-graded monad; replace it with a budget monoid and you get a resource-graded monad.
          Same theorem, same proof obligations, new discipline.
        </li>
      </ul>
      <p>Try the interactive: pick a grade for each Kleisli step and watch the composite rise.</p>
      <GradeLadderInteractive />
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "Graded monads and parametric effects (PRECOMMIT context).",
    },
    {
      label: "GradedMonad (MathLib, Agda)",
      path: "agda/MathLib/Monad/GradedMonad.agda",
    },
    {
      label: "GradedMonad (MathLibCore, Agda)",
      path: "agda/MathLibCore/CategoryTheory/GradedMonad.agda",
      note: "Direct agda-categories graded monad surface used by PRECOMMIT.",
    },
    {
      label: "PRECOMMIT graded monad (Agda)",
      path: "agda/KernelAdjacent/PRECOMMIT/GradedMonad.agda",
    },
    {
      label: "Policy graded monad (Kernel)",
      path: "agda/Kernel/PRECOMMIT/PolicyGradedMonad.agda",
    },
    {
      label: "Orchard–Wadler–Eades — Unifying Graded and Parameterised Monads",
      path: "https://arxiv.org/abs/2001.10274",
      note: "External reference cited from Monads-And-Algebras.",
    },
    {
      label: "Katsumata — Parametric Effect Monads",
      path: "https://dl.acm.org/doi/10.1145/2535838.2535846",
      note: "POPL 2014: foundational graded-effects paper.",
    },
  ],
};

export function GradedMonadExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<GradeLadderDiagram rungs={LADDER_RUNGS} accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
